import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Send, Loader2, MessageCircleQuestion } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const SUGGESTIONS = [
  "Når er mitt neste styremøte?",
  "Hvilke åpne oppgaver har jeg?",
  "Hva bør stå på sakslisten til et ordinært styremøte?",
  "Hvor stor eierandel har jeg totalt?",
];

export function AiAssistant() {
  const [messages, setMessages] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const ask = async (question) => {
    const text = (question ?? q).trim();
    if (!text || loading) return;
    setQ("");
    setMessages((m) => [...m, { role: "user", text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/assistant/ask", { question: text });
      setMessages((m) => [...m, { role: "assistant", text: data.answer }]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setMessages((m) => [...m, { role: "assistant", text: "Beklager, jeg klarte ikke å svare akkurat nå. Prøv igjen." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="ai-assistant" className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm">
      <div className="flex items-center gap-3 border-b border-border/60 bg-primary px-5 py-4 text-primary-foreground">
        <div className="grid h-9 w-9 place-items-center rounded-xl bg-lime text-lime-foreground">
          <Sparkles className="h-5 w-5" strokeWidth={1.9} />
        </div>
        <div>
          <h2 className="font-headings text-base font-bold tracking-tight">Spør AI-styreassistenten</h2>
          <p className="text-xs text-primary-foreground/70">Svarer på spørsmål om selskapene og styrearbeidet ditt</p>
        </div>
      </div>

      <div ref={scrollRef} className="max-h-80 min-h-[128px] space-y-3 overflow-y-auto px-5 py-4" data-testid="ai-assistant-messages">
        {messages.length === 0 && !loading ? (
          <div className="py-2">
            <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <MessageCircleQuestion className="h-4 w-4" /> Prøv for eksempel:
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  data-testid="ai-suggestion-chip"
                  onClick={() => ask(s)}
                  className="rounded-full border border-border bg-secondary px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/40 hover:bg-lime/20"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {messages.map((msg, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              data-testid={`ai-message-${msg.role}`}
              className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                msg.role === "user"
                  ? "rounded-br-sm bg-primary text-primary-foreground"
                  : "rounded-bl-sm border border-border/60 bg-secondary text-foreground"
              }`}
            >
              {msg.text}
            </div>
          </motion.div>
        ))}

        {loading ? (
          <div className="flex justify-start">
            <div className="flex items-center gap-2 rounded-2xl rounded-bl-sm border border-border/60 bg-secondary px-4 py-2.5 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Tenker …
            </div>
          </div>
        ) : null}
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); ask(); }}
        className="flex items-center gap-2 border-t border-border/60 px-4 py-3"
      >
        <Input
          data-testid="ai-assistant-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Skriv et spørsmål …"
          className="h-11"
        />
        <Button type="submit" disabled={loading || !q.trim()} data-testid="ai-assistant-send" className="h-11 shrink-0 gap-2 active:scale-[0.98]">
          <Send className="h-4 w-4" /> Spør
        </Button>
      </form>
    </div>
  );
}
